import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../../../../../contexts/ThemeContext';
import { cn } from '../../../../../lib/utils';
import { API_BASE_URL, API_ENDPOINTS } from '../../../../../config/apiConfig';
import { useFlexibleList } from '../../../../../hooks/useSWR';
import { Modal, ModalHeader, ModalTitle, ModalDescription, ModalContent, ModalFooter, TextInput, DateInput, Textarea, Button, Label, Select } from '../../../../../components/ui';
import FormFileUpload from '../../../../../components/ui/FormFileUpload';
import FileListModal from '../../../../../components/ui/FileListModal';
import { getLocalIPAddress } from '../../../../../utils/getLocalIP';
import { useDeleteMultipleFileAttachments } from '../../../../../hooks/useFileAttachments';
import { ApprovalIntegration } from '../../../../../components/generic';
import { DocumentTextIcon, FileIcon, PublishIcon } from '../../../../../components/ui/Icons';
import { revisionConfig } from '../revisionConfig';
import { documentConfig } from '../../documentConfig';

const DocumentRevisionsTab = ({ 
  selectedRecord,
  onRevisionUpdate, // Callback to refresh Document data after revision changes
  config,
}) => {
  const { theme } = useTheme();
  const revisionFileAccept =
    config?.features?.revisionFileAccept || '.pdf,.doc,.docx,.txt,.md,image/*';
  const isPdfOnlyRevisionUpload =
    String(revisionFileAccept)
      .toLowerCase()
      .split(',')
      .every((part) => {
        const token = part.trim();
        return token === '.pdf' || token === 'application/pdf';
      });
  const revisionFilePlaceholder = isPdfOnlyRevisionUpload
    ? 'Drop PDF files here or click to browse'
    : 'Drop files here or click to browse';
  const revisionSupportedFormatsLabel = isPdfOnlyRevisionUpload
    ? 'Supported formats: PDF'
    : 'Supported formats: PDF, DOC, DOCX, TXT, MD, Images (JPG, PNG, GIF, WebP)';
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [, setEditingRevision] = useState(null);
  const [modalMode, setModalMode] = useState('create');
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [filesToDelete, setFilesToDelete] = useState([]);
  const [revisionFileAttachments, setRevisionFileAttachments] = useState({});
  const [fileModalOpen, setFileModalOpen] = useState(false);
  const [selectedRevisionFiles, setSelectedRevisionFiles] = useState([]);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishForm, setPublishForm] = useState({
    publishedAt: '',
    publishedReason: '',
  });
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishError, setPublishError] = useState(null);

  // Fetch users for the issuedById select
  const { data: users = [], isLoading: usersLoading, error: usersError } = useFlexibleList('/users');
  const { data: documentStatuses = [] } = useFlexibleList('/document-status');

  const publishedStatusRecord = useMemo(
    () => documentStatuses.find((s) => s.name === 'Published'),
    [documentStatuses]
  );

  // Use SWR to fetch Document revisions
  const { data: documentRevisions = [], isLoading, error, mutate: refreshRevisions } = useFlexibleList(
    selectedRecord?.id ? `/document-management/revision/by-document/${selectedRecord.id}` : null
  );

  // Fetch file attachments for each revision when revisions are loaded
  useEffect(() => {
    const fetchFileAttachments = async () => {
      if (documentRevisions && documentRevisions.length > 0) {
        const attachmentsMap = {};
        for (const revision of documentRevisions) {
          const attachments = await getFileAttachmentsForRevision(revision.id);
          attachmentsMap[revision.id] = attachments;
        }
        setRevisionFileAttachments(attachmentsMap);
      }
    };

    fetchFileAttachments();
  }, [documentRevisions]);

  // File attachment hooks
  const { deleteMultipleFileAttachments } = useDeleteMultipleFileAttachments();

  // Function to fetch file attachments for a specific revision
  // Note: Revisions use entityTypeId 2, but the endpoint filters by isDeleted: false
  const getFileAttachmentsForRevision = async (revisionId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/file-attachment/entity/2/${revisionId}`, {
        headers: {
          ...(localStorage.getItem('authToken') ? { Authorization: `Bearer ${localStorage.getItem('authToken')}` } : {})
        }
      });
      if (response.ok) {
        const attachments = await response.json();
        // Filter out deleted files as an extra safety measure
        return attachments.filter(file => !file.isDeleted);
      }
      return [];
    } catch (error) {
      console.error('Error fetching file attachments:', error);
      return [];
    }
  };



  const getNextVersion = () => {
    if (!documentRevisions || documentRevisions.length === 0) {
      return '1.0';
    }
    
    // Find the highest version number
    const versions = documentRevisions
      .map(rev => rev.version)
      .filter(version => /^[0-9]+(\.[0-9]+)*$/.test(version))
      .map(version => version.split('.').map(Number));
    
    if (versions.length === 0) {
      return '1.0';
    }
    
    // Sort versions and get the highest
    versions.sort((a, b) => {
      for (let i = 0; i < Math.max(a.length, b.length); i++) {
        const aVal = a[i] || 0;
        const bVal = b[i] || 0;
        if (aVal !== bVal) return bVal - aVal;
      }
      return 0;
    });
    
    const highest = versions[0];
    // Increment the minor version
    highest[1] = (highest[1] || 0) + 1;
    return highest.join('.');
  };

  const handleAddRevision = () => {
    setEditingRevision(null);
    setModalMode('create');
    setFormData({
      version: getNextVersion(),
      dateIssued: '',
      issuedById: '',
      dateEffective: '',
      dateExpired: '',
      notes: '',
      fileAttachments: []
    });
    setErrors({});
    setFilesToDelete([]);
    setIsModalOpen(true);
  };

  const handleEditRevision = async (revision) => {
    console.log('Revision Edit - Initial Data JSON:', JSON.stringify(revision, null, 2));
    setEditingRevision(revision);
    setModalMode('edit');
    
    setFormData({
      id: revision.id,
      version: revision.version || '',
      dateIssued: revision.dateIssued ? new Date(revision.dateIssued).toISOString().slice(0, 16) : '',
      issuedById: revision.issuedById || '',
      dateEffective: revision.dateEffective ? new Date(revision.dateEffective).toISOString().slice(0, 16) : '',
      dateExpired: revision.dateExpired ? new Date(revision.dateExpired).toISOString().slice(0, 16) : '',
      notes: revision.notes || '',
      fileAttachments: [] // New files to upload
    });
    setErrors({});
    setFilesToDelete([]);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingRevision(null);
    setFormData({});
    setErrors({});
    setIsSubmitting(false);
    setFilesToDelete([]);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Version validation
    if (!formData.version?.trim()) {
      newErrors.version = 'Version is required';
    } else if (!/^[0-9]+(\.[0-9]+)*$/.test(formData.version)) {
      newErrors.version = 'Version must be in format like 1.0, 2.1, etc.';
    }
    
    // Notes validation - now optional
    // No validation needed for notes as it's optional
    
    // Date validation - ensure effective date is not before issued date
    if (formData.dateIssued && formData.dateEffective) {
      const issuedDate = new Date(formData.dateIssued);
      const effectiveDate = new Date(formData.dateEffective);
      if (effectiveDate < issuedDate) {
        newErrors.dateEffective = 'Effective date cannot be before issued date';
      }
    }
    
    // Date validation - ensure expiration date is not before effective date
    if (formData.dateEffective && formData.dateExpired) {
      const effectiveDate = new Date(formData.dateEffective);
      const expiredDate = new Date(formData.dateExpired);
      if (expiredDate < effectiveDate) {
        newErrors.dateExpired = 'Expiration date cannot be before effective date';
      }
    }
    
    // User validation - ensure issuedById exists in users list if provided
    if (formData.issuedById && formData.issuedById !== '') {
      const userExists = users.some(user => user.id === parseInt(formData.issuedById));
      if (!userExists) {
        newErrors.issuedById = 'Selected user does not exist';
      }
    }
    
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // For updates, exclude the id, fileAttachments, and existingFiles from the payload since they're handled separately
      const { id, fileAttachments, existingFiles, ...formDataWithoutIdAndFiles } = formData;
      
      const payload = {
        ...formDataWithoutIdAndFiles,
        documentId: selectedRecord.id,
        // Convert datetime-local values to ISO strings
        dateIssued: formData.dateIssued ? new Date(formData.dateIssued).toISOString() : null,
        dateEffective: formData.dateEffective ? new Date(formData.dateEffective).toISOString() : null,
        dateExpired: formData.dateExpired ? new Date(formData.dateExpired).toISOString() : null,
        // Convert empty strings to null for optional foreign keys
        issuedById: formData.issuedById && formData.issuedById !== '' ? parseInt(formData.issuedById) : null
      };

      console.log('Revision Edit - Post Data JSON:', JSON.stringify(payload, null, 2));
      
      const url = modalMode === 'create' 
        ? `${API_BASE_URL}/document-management/revision`
        : `${API_BASE_URL}/document-management/revision/${formData.id}`;
      
      const method = modalMode === 'create' ? 'POST' : 'PATCH';
      
      // Get user ID from localStorage for audit tracking
      const savedUser = localStorage.getItem('user');
      let userId = null;
      
      if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
        try {
          const parsedUser = JSON.parse(savedUser);
          userId = parsedUser.id?.toString() || null;
        } catch (parseError) {
          userId = null;
        }
      }

      // Debug logging
      console.log('DocumentRevisionsTab - User ID from localStorage:', userId);
      console.log('DocumentRevisionsTab - Saved user data:', savedUser);

      // Get local IP address for audit tracking
      const localIP = await getLocalIPAddress();

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'user-id': userId, // Required for audit tracking (capitalized)
          ...(localIP && { 'X-Local-IP': localIP }), // Local IP address (capitalized)
          ...(localStorage.getItem('authToken') ? { Authorization: `Bearer ${localStorage.getItem('authToken')}` } : {})
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const savedRevision = await response.json();
        
        // Handle file operations using the same approach as GenericFormsPage
        if ((formData.fileAttachments && formData.fileAttachments.length > 0) || (filesToDelete && filesToDelete.length > 0)) {
          try {
            // Handle file uploads
            if (formData.fileAttachments && formData.fileAttachments.length > 0) {
              
              // Use the revision-specific file upload endpoint
              const formDataForUpload = new FormData();
              
              // Add all files - extract actual File objects from fileAttachments
              formData.fileAttachments.forEach((fileAttachment, index) => {
                // Extract the actual File object from the fileAttachment
                const actualFile = fileAttachment.file || fileAttachment;
                
                if (actualFile instanceof File) {
                  formDataForUpload.append('files', actualFile);
                }
              });
              
              // Add metadata
              formDataForUpload.append('entityTypeId', '1'); // Document entity type
              formDataForUpload.append('entityId', savedRevision.id.toString());
              formDataForUpload.append('description', '');
              formDataForUpload.append('isPrivate', 'false');
              formDataForUpload.append('masterEntityTypeId', '1'); // Document master

              let response = await fetch(`${API_BASE_URL}/document-management/revision/${savedRevision.id}/upload-multiple-files`, {
                method: 'POST',
                headers: {
                  ...(userId ? { 'user-id': userId } : {}),
                  ...(localStorage.getItem('authToken') ? { Authorization: `Bearer ${localStorage.getItem('authToken')}` } : {})
                },
                body: formDataForUpload
              });

              // If the main endpoint fails, try the alternative endpoint
              if (!response.ok) {
                // Create new FormData for alternative endpoint (uses 'file' instead of 'files')
                const alternativeFormData = new FormData();
                formData.fileAttachments.forEach((fileAttachment, index) => {
                  // Extract the actual File object from the fileAttachment
                  const actualFile = fileAttachment.file || fileAttachment;
                  
                  if (actualFile instanceof File) {
                    alternativeFormData.append('file', actualFile);
                  }
                });
                
                // Add metadata
                alternativeFormData.append('entityTypeId', '1');
                alternativeFormData.append('entityId', savedRevision.id.toString());
                alternativeFormData.append('description', '');
                alternativeFormData.append('isPrivate', 'false');
                alternativeFormData.append('masterEntityTypeId', '1');

                response = await fetch(`${API_BASE_URL}/document-management/revision/${savedRevision.id}/upload-files`, {
                  method: 'POST',
                  headers: {
                    ...(userId ? { 'user-id': userId } : {}),
                    ...(localStorage.getItem('authToken') ? { Authorization: `Bearer ${localStorage.getItem('authToken')}` } : {})
                  },
                  body: alternativeFormData
                });
              }

              if (!response.ok) {
                const errorData = await response.json();
                throw new Error(`File upload failed: ${errorData.message || response.statusText}`);
              }

              console.log('Files uploaded successfully for revision');
            }
            
            // Handle file deletions
            if (filesToDelete && filesToDelete.length > 0) {
              console.log('Deleting files for revision:', filesToDelete);
              
              const fileIds = filesToDelete.map(file => Number(file.id));
              await deleteMultipleFileAttachments(fileIds, 1, savedRevision.id);
              setFilesToDelete([]);
              
              console.log('Files deleted successfully for revision');
            }
          } catch (error) {
            console.error('Error handling file operations:', error);
            // Don't fail the revision creation if file operations fail
          }
        }

        handleModalClose();
        refreshRevisions();
        
        // Refresh file attachments for the updated revision
        if (savedRevision && savedRevision.id) {
          const attachments = await getFileAttachmentsForRevision(savedRevision.id);
          setRevisionFileAttachments(prev => ({
            ...prev,
            [savedRevision.id]: attachments
          }));
        }
        
        // Also refresh the Document data to show updated revisions count
        if (onRevisionUpdate) {
          onRevisionUpdate();
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save revision');
      }
    } catch (error) {
      console.error('Error saving revision:', error);
      setErrors({ submit: error.message || 'Failed to save revision' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRevision = async (revisionId) => {
    if (!window.confirm('Are you sure you want to delete this revision?')) {
      return;
    }

    try {
      // Get user ID from localStorage for audit tracking
      const savedUser = localStorage.getItem('user');
      let userId = null;
      
      if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
        try {
          const parsedUser = JSON.parse(savedUser);
          userId = parsedUser.id?.toString() || null;
        } catch (parseError) {
          userId = null;
        }
      }

      const response = await fetch(`${API_BASE_URL}/document-management/revision/${revisionId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'user-id': userId, // Required for audit tracking
          ...(localStorage.getItem('authToken') ? { Authorization: `Bearer ${localStorage.getItem('authToken')}` } : {})
        }
      });

      if (response.ok) {
        // Refresh the Document revisions using SWR
        refreshRevisions();
        
        // Remove file attachments for the deleted revision
        setRevisionFileAttachments(prev => {
          const updated = { ...prev };
          delete updated[revisionId];
          return updated;
        });
        
        // Also refresh the Document data
        if (onRevisionUpdate) {
          onRevisionUpdate();
        }
      } else {
        throw new Error('Failed to delete revision');
      }
    } catch (error) {
      console.error('Error deleting revision:', error);
      alert('Failed to delete revision');
    }
  };

  const handleSetCurrentRevision = async (revisionId) => {
    if (!selectedRecord?.id) {
      alert('No document selected');
      return;
    }

    // Check if this revision is already the current one
    if (selectedRecord.currentRevisionId === revisionId) {
      alert('This revision is already set as the current revision');
      return;
    }

    if (!window.confirm('Are you sure you want to set this revision as the current revision?')) {
      return;
    }

    try {
      // Get user ID from localStorage for audit tracking
      const savedUser = localStorage.getItem('user');
      let userId = null;
      
      if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
        try {
          const parsedUser = JSON.parse(savedUser);
          userId = parsedUser.id || null;
        } catch (parseError) {
          userId = null;
        }
      }

      if (!userId) {
        alert('User ID not found. Please log in again.');
        return;
      }

      // Get local IP address for audit tracking
      const localIP = await getLocalIPAddress();

      const endpoint = documentConfig.api.endpoints.setCurrentRevision(selectedRecord.id);
      const response = await fetch(`${API_BASE_URL}${documentConfig.api.baseEndpoint}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user-id': userId.toString(),
          ...(localIP && { 'X-Local-IP': localIP }),
          ...(localStorage.getItem('authToken') ? { Authorization: `Bearer ${localStorage.getItem('authToken')}` } : {})
        },
        body: JSON.stringify({
          revisionId: revisionId,
          userId: userId,
          reason: 'Set as current revision'
        })
      });

      if (response.ok) {
        // Refresh the Document data to get updated currentRevisionId
        if (onRevisionUpdate) {
          onRevisionUpdate();
        }
        
        // Refresh revisions list
        refreshRevisions();
        
        alert('Revision set as current successfully');
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Failed to set current revision' }));
        throw new Error(errorData.message || 'Failed to set current revision');
      }
    } catch (error) {
      console.error('Error setting current revision:', error);
      alert(error.message || 'Failed to set current revision');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const toDateInputValue = (date) => {
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const getUserFromStorage = () => {
    const savedUser = localStorage.getItem('user');
    if (!savedUser) return null;
    try {
      return JSON.parse(savedUser);
    } catch {
      return null;
    }
  };

  const getUserId = () => {
    const user = getUserFromStorage();
    return user?.id != null ? parseInt(user.id, 10) : null;
  };

  const openPublishDialog = () => {
    setPublishError(null);
    if (!publishedStatusRecord?.id) {
      setPublishError(
        'Published status is not configured. Add an active "Published" document status in admin.'
      );
    }
    setPublishForm({
      publishedAt: toDateInputValue(new Date()),
      publishedReason: '',
    });
    setPublishDialogOpen(true);
  };

  const closePublishDialog = () => {
    setPublishDialogOpen(false);
    setPublishForm({ publishedAt: '', publishedReason: '' });
    setPublishError(null);
  };

  const currentRevisionForPublish = documentRevisions.find(
    (r) => r.id === selectedRecord?.currentRevisionId
  );

  const handlePublish = async () => {
    if (!selectedRecord?.id) return;
    const userId = getUserId();
    if (!userId) {
      setPublishError('You must be logged in to publish.');
      return;
    }
    if (!selectedRecord.currentRevisionId) {
      setPublishError('Set a current revision before publishing.');
      return;
    }
    if (!publishForm.publishedAt) {
      setPublishError('Published date is required.');
      return;
    }
    if (!publishedStatusRecord?.id) {
      setPublishError(
        'Published status is not configured. Add an active "Published" document status in admin.'
      );
      return;
    }

    setIsPublishing(true);
    setPublishError(null);
    try {
      const publishPath = documentConfig.api.endpoints.publish(selectedRecord.id);
      const response = await fetch(`${API_BASE_URL}${documentConfig.api.baseEndpoint}${publishPath}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('authToken')
            ? { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
            : {}),
          'user-id': String(userId),
        },
        body: JSON.stringify({
          userId,
          reason: publishForm.publishedReason?.trim() || undefined,
          publishedAt: (() => {
            const [y, m, d] = publishForm.publishedAt.split('-').map(Number);
            return new Date(y, m - 1, d).toISOString();
          })(),
          statusId: publishedStatusRecord.id,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to publish document');
      }

      const updated = await response.json();
      closePublishDialog();
      if (onRevisionUpdate) {
        onRevisionUpdate(updated);
      }
    } catch (error) {
      setPublishError(error.message || 'Failed to publish');
    } finally {
      setIsPublishing(false);
    }
  };

  const isPublished = Boolean(selectedRecord?.publishedAt);
  const canPublish =
    selectedRecord?.id &&
    selectedRecord?.currentRevisionId &&
    !isPublished;

  const getStatusBadge = (revision) => {
    const now = new Date();
    const effective = revision.dateEffective ? new Date(revision.dateEffective) : null;
    const expired = revision.dateExpired ? new Date(revision.dateExpired) : null;
    const issued = revision.dateIssued ? new Date(revision.dateIssued) : null;

    if (expired && now > expired) {
      return { text: 'Expired', color: 'bg-red-100 text-red-800' };
    } else if (effective && now < effective) {
      return { text: 'Future', color: 'bg-yellow-100 text-yellow-800' };
    } else if (issued && now >= issued) {
      return { text: 'Active', color: 'bg-green-100 text-green-800' };
    } else {
      return { text: 'Draft', color: 'bg-gray-100 text-gray-800' };
    }
  };


  return (
    <>
      <div 
        className="p-4 flex-1 overflow-y-auto advanced-table-scroll space-y-4"
        style={{ 
          scrollbarWidth: 'thin',
          minHeight: 0,
          maxHeight: '100%'
        }}
      >
        {/* Publish — required for My Procedures viewers; not the same as revision Active badge */}
        {selectedRecord?.id && (
          <div
            className={cn(
              'rounded-lg border p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3',
              isPublished
                ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                : 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20'
            )}
          >
            <div className="text-sm">
              {isPublished ? (
                <p className="text-green-800 dark:text-green-200">
                  <strong>Published</strong>
                  {' — '}
                  {formatDate(selectedRecord.publishedAt)}
                  {selectedRecord.publishedReason
                    ? ` · ${selectedRecord.publishedReason}`
                    : ''}
                  . Assigned viewers can open this in My Procedures.
                </p>
              ) : (
                <p className="text-amber-800 dark:text-amber-200">
                  <strong>Not published yet.</strong> A revision can show <em>Active</em> here, but
                  viewers only see the procedure in <strong>My Procedures</strong> after you{' '}
                  <strong>Publish</strong> (uses the current revision).
                </p>
              )}
              {publishError && (
                <p className="text-red-600 dark:text-red-400 text-xs mt-1">{publishError}</p>
              )}
            </div>
            {canPublish && (
              <Button
                type="button"
                size="sm"
                className="shrink-0 gap-2"
                onClick={openPublishDialog}
              >
                <PublishIcon className="w-4 h-4" />
                Publish
              </Button>
            )}
          </div>
        )}

        {/* Header with Add Button */}
        <div className="flex items-center justify-between">
          <div className={cn("text-sm font-medium", theme.text.primary)}>
            Document Revisions ({documentRevisions.length})
          </div>
          <button
            onClick={handleAddRevision}
            className={cn(
              "flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
              theme.colors.primary,
              theme.text.inverse,
              "hover:opacity-90"
            )}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Revision
          </button>
        </div>

        {/* Document Revisions List */}
        <div className="space-y-4">
          {/* Loading State */}
          {isLoading && (
            <div className={cn("text-center py-8", theme.text.muted)}>
              <div className="mb-4">
                <svg className="animate-spin w-8 h-8 mx-auto" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
              <div className="text-sm">Loading revisions...</div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className={cn("text-center py-8", theme.text.muted)}>
              <div className="mb-4">
                <svg className="w-8 h-8 mx-auto text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="text-sm text-red-600 mb-2">Error loading revisions</div>
              <div className="text-xs text-gray-500">{error.message || error}</div>
              <button
                onClick={refreshRevisions}
                className={cn("mt-2 px-3 py-1 text-xs rounded border", theme.border.light, theme.text.secondary)}
              >
                Retry
              </button>
            </div>
          )}

          {/* Data List */}
          {!isLoading && !error && documentRevisions.length > 0 ? (
            <div className="space-y-3">
              {documentRevisions
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) // Sort by newest first
                .map((revision, index) => {
                  const status = getStatusBadge(revision);
                  const isCurrentRevision = selectedRecord?.currentRevisionId === revision.id;
                  return (
                    <div key={revision.id || index} className={cn(
                      "p-4 rounded-lg border transition-colors duration-200",
                      isCurrentRevision 
                        ? "border-blue-500 border-2 bg-blue-50 dark:bg-blue-900/20" 
                        : theme.border.subtle,
                      theme.colors.background,
                      "hover:border-opacity-60"
                    )}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          {/* Header with version and status */}
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <div className="flex items-center gap-2">
                              {isCurrentRevision && (
                                <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                              )}
                              <div className={cn(
                                "text-sm font-medium",
                                isCurrentRevision ? "text-blue-700 dark:text-blue-300 font-semibold" : theme.text.primary
                              )}>
                                Version {revision.version}
                                {isCurrentRevision && (
                                  <span className="ml-2 text-xs font-normal text-blue-600 dark:text-blue-400">
                                    (Current Revision)
                                  </span>
                                )}
                              </div>
                              {isCurrentRevision && (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-600 text-white dark:bg-blue-500 dark:text-white shadow-sm">
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                  Current
                                </span>
                              )}
                              <span className={cn("inline-flex items-center px-2 py-1 rounded-full text-xs font-medium", status.color)}>
                                {status.text}
                              </span>
                            </div>
                            
                            {/* Action buttons */}
                            <div className="flex items-center gap-2">
                              {selectedRecord?.currentRevisionId !== revision.id && (
                                <button
                                  onClick={() => handleSetCurrentRevision(revision.id)}
                                  className={cn("p-1 rounded hover:bg-blue-100 dark:hover:bg-blue-900/20 text-blue-600 dark:text-blue-400")}
                                  title="Set as current revision"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </button>
                              )}
                              <button
                                onClick={() => handleEditRevision(revision)}
                                className={cn("p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700", theme.text.secondary)}
                                title="Edit revision"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDeleteRevision(revision.id)}
                                className={cn("p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400")}
                                title="Delete revision"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                          
                          {/* Revision notes */}
                          {revision.notes && (
                            <div className="mb-3">
                              <div className={cn("text-xs font-medium mb-1", theme.text.secondary)}>Notes:</div>
                              <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                                {revision.notes}
                              </div>
                            </div>
                          )}

                          {/* File Attachments */}
                          {revisionFileAttachments[revision.id] && revisionFileAttachments[revision.id].filter(file => !file.isDeleted).length > 0 && (
                            <div className="mb-3">
                              <div className={cn("text-xs font-medium mb-2", theme.text.secondary)}>Attachments:</div>
                              <div className="flex items-center gap-2">
                                <FileIcon className="w-4 h-4 text-blue-500" />
                                <span className={cn("text-xs font-medium", theme.text.primary)}>
                                  {revisionFileAttachments[revision.id].filter(file => !file.isDeleted).length} file{revisionFileAttachments[revision.id].filter(file => !file.isDeleted).length !== 1 ? 's' : ''}
                                </span>
                                {revisionFileAttachments[revision.id].filter(file => !file.isDeleted).length === 1 ? (
                                  <a
                                    href={API_ENDPOINTS.fileAttachment.download(revisionFileAttachments[revision.id].filter(file => !file.isDeleted)[0].id)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={cn(
                                      "text-xs px-2 py-1 rounded-md transition-colors",
                                      "bg-blue-600 text-white hover:bg-blue-700",
                                      "cursor-pointer"
                                    )}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    View
                                  </a>
                                ) : (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedRevisionFiles(revisionFileAttachments[revision.id].filter(file => !file.isDeleted));
                                      setFileModalOpen(true);
                                    }}
                                    className={cn(
                                      "text-xs px-2 py-1 rounded-md transition-colors",
                                      "bg-blue-600 text-white hover:bg-blue-700",
                                      "cursor-pointer"
                                    )}
                                  >
                                    View All
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Data fields grid */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                            {/* Column 1 */}
                            <div className="space-y-2">
                              <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                                <span className="font-medium">Issued:</span>
                                <span>{formatDate(revision.dateIssued)}</span>
                              </div>
                              
                              {revision.issuedBy && (
                                <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                                  <span className="font-medium">Issued By:</span>
                                  <span>{revision.issuedBy.userDisplayName || `${revision.issuedBy.userFirstName} ${revision.issuedBy.userLastName}`.trim()}</span>
                                </div>
                              )}
                              
                              <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                                <span className="font-medium">Effective:</span>
                                <span>{formatDate(revision.dateEffective)}</span>
                              </div>
                            </div>

                            {/* Column 2 */}
                            <div className="space-y-2">
                              <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                                <span className="font-medium">Expires:</span>
                                <span>{formatDate(revision.dateExpired)}</span>
                              </div>
                              
                              <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                                <span className="font-medium">Created:</span>
                                <span>{formatDate(revision.createdAt)}</span>
                              </div>
                            </div>
                          </div>

                          {/* Revision Review and Approval Integration */}
                          {revisionConfig.shouldShowApprovalInDetails() && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <ApprovalIntegration 
                                entity={revision}
                                // Also pass the document so we can get approvalTemplateId from it
                                document={selectedRecord}
                                entityType="document_revision"
                                entityData={{
                                  documentCode: selectedRecord.code,
                                  documentTitle: selectedRecord.title,
                                  revisionNumber: revision.revisionNumber,
                                  revisionDate: revision.revisionDate,
                                  changeDescription: revision.changeDescription,
                                  documentType: selectedRecord.documentType?.name,
                                  department: selectedRecord.department?.name
                                }}
                                onApprovalUpdate={(approval) => {
                                  console.log('Revision review and approval updated:', approval);
                                  // You can add additional logic here to refresh the revision data
                                }}
                                customTitle="Revision Review and Approval Status"
                                customButtonText={revisionConfig.approval.approvalButtonText}
                                customIcon={DocumentTextIcon}
                                showEntityDetails={revisionConfig.approval.showApprovalStatus}
                                showApprovalActions={revisionConfig.approval.showApprovalActions}
                                showApprovalHistory={revisionConfig.approval.showApprovalHistory}
                                formMode={revisionConfig.approval.formMode || 'standalone'}
                                config={{
                                  defaultApprovalType: revisionConfig.approval.defaultApprovalType,
                                  allowMultipleApprovals: revisionConfig.approval.allowMultipleApprovals
                                }}
                              />
                            </div>
                          )}

                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            !isLoading && !error && (
              <div className={cn("text-center py-8", theme.text.muted)}>
                <div className="mb-4">
                  <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                </div>
                <div className="text-sm font-medium mb-2">No revisions found</div>
                <div className="text-xs">Click 'Add Revision' to create the first revision for this document</div>
              </div>
            )
          )}
        </div>
      </div>

      {/* Custom Modal using UI Components */}
      <Modal isOpen={isModalOpen} onClose={handleModalClose} size="lg">
        <ModalHeader onClose={handleModalClose}>
          <ModalTitle>
            {modalMode === 'create' ? 'Add New Revision' : 'Edit Revision'}
          </ModalTitle>
          <ModalDescription>
            {modalMode === 'create' 
              ? 'Create a new revision for this document'
              : 'Update the revision details'
            }
          </ModalDescription>
        </ModalHeader>
        
        <ModalContent>
          <div className="flex-1 overflow-y-auto modal-form-scroll" style={{ maxHeight: '60vh' }}>
            <div className="space-y-6">
            {/* Revision Information Section */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-base">📄</span>
                <h4 className={cn("text-sm font-semibold", theme.text.primary)}>Revision Information</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Version */}
                <div>
                  <Label htmlFor="version" className="text-sm font-medium">
                    Version <span className="text-red-500">*</span>
                  </Label>
                  <TextInput
                    id="version"
                    name="version"
                    value={formData.version || ''}
                    onChange={(e) => handleInputChange('version', e.target.value)}
                    placeholder="e.g., 1.0, 2.1, 3.0"
                    required
                    error={errors.version}
                    className="mt-1"
                  />
                </div>
                
                {/* Date Issued */}
                <div>
                  <Label htmlFor="dateIssued" className="text-sm font-medium">
                    Date Issued
                  </Label>
                  <DateInput
                    id="dateIssued"
                    name="dateIssued"
                    value={formData.dateIssued || ''}
                    onChange={(e) => handleInputChange('dateIssued', e.target.value)}
                    className="mt-1"
                  />
                </div>
                
                {/* Issued By */}
                <div>
                  <Label htmlFor="issuedById" className="text-sm font-medium">
                    Issued By
                  </Label>
                  <Select
                    name="issuedById"
                    value={formData.issuedById || ''}
                    onChange={(e) => handleInputChange('issuedById', e.target.value)}
                    placeholder={usersLoading ? "Loading users..." : "Select user..."}
                    className="mt-1"
                    disabled={usersLoading}
                  >
                    {usersLoading ? (
                      <option value="" disabled>Loading users...</option>
                    ) : usersError ? (
                      <option value="" disabled>Error loading users</option>
                    ) : users.length === 0 ? (
                      <option value="" disabled>No users found</option>
                    ) : (
                      users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.userDisplayName || `${user.userFirstName} ${user.userLastName}`.trim() || user.userEmail}
                        </option>
                      ))
                    )}
                  </Select>
                  {usersError && (
                    <div className="text-xs text-red-500 mt-1">
                      Error loading users: {usersError.message}
                    </div>
                  )}
                  {errors.issuedById && (
                    <div className="text-xs text-red-500 mt-1">
                      {errors.issuedById}
                    </div>
                  )}
                </div>
                
                {/* Date Effective */}
                <div>
                  <Label htmlFor="dateEffective" className="text-sm font-medium">
                    Effective Date
                  </Label>
                  <DateInput
                    id="dateEffective"
                    name="dateEffective"
                    value={formData.dateEffective || ''}
                    onChange={(e) => handleInputChange('dateEffective', e.target.value)}
                    className="mt-1"
                    error={errors.dateEffective}
                  />
                </div>
                
                {/* Date Expired */}
                <div>
                  <Label htmlFor="dateExpired" className="text-sm font-medium">
                    Expiration Date
                  </Label>
                  <DateInput
                    id="dateExpired"
                    name="dateExpired"
                    value={formData.dateExpired || ''}
                    onChange={(e) => handleInputChange('dateExpired', e.target.value)}
                    className="mt-1"
                    error={errors.dateExpired}
                  />
                </div>
              </div>
            </div>
            
            {/* Details Section */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-base">📝</span>
                <h4 className={cn("text-sm font-semibold", theme.text.primary)}>Details</h4>
              </div>
              <div className="space-y-4">
                {/* Revision Notes */}
                <div>
                  <Label htmlFor="notes" className="text-sm font-medium">
                    Revision Notes
                  </Label>
                  <Textarea
                    id="notes"
                    name="notes"
                    value={formData.notes || ''}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    placeholder="Describe the changes made in this revision..."
                    rows={4}
                    className="mt-1"
                    error={errors.notes}
                  />
                </div>
              </div>
            </div>

            {/* File Attachments Section */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-base">📎</span>
                <h4 className={cn("text-sm font-semibold", theme.text.primary)}>File Attachments</h4>
              </div>
              <div className="space-y-4">
                <FormFileUpload
                  name="fileAttachments"
                  value={Array.isArray(formData.fileAttachments) ? formData.fileAttachments : []}
                  onChange={async (e) => {
                    // For fileUpload, the value is already the file attachments array/object
                    const newValue = e.target.value || e.target.fileAttachments || [];
                    
                    
                    setFormData(prev => ({ ...prev, fileAttachments: newValue }));
                    
                    // Skip validation for fileUpload fields
                    setErrors(prev => ({ ...prev, fileAttachments: '' }));
                  }}
                  label="Upload Files"
                  required={false}
                  error={errors.fileAttachments || null}
                  multiple={true}
                  accept={revisionFileAccept}
                  maxSize={50 * 1024 * 1024} // 50MB
                  maxFiles={10}
                  placeholder={revisionFilePlaceholder}
                  showPreview={true}
                  disabled={false}
                  readOnly={false}
                  // Entity-specific props
                  entityTypeId={1} // Document entity type
                  entityId={formData.id || null} // Use the current revision ID (null for new revisions)
                  userId={null} // No default user ID
                  description=""
                  isPrivate={false}
                  // Callbacks
                  onFileUpload={null}
                  onFileRemove={null}
                  onFilesToDeleteChange={(filesToDelete) => {
                    setFilesToDelete(filesToDelete);
                  }}
                  onError={(error) => {
                    setErrors(prev => ({ ...prev, fileAttachments: error }));
                  }}
                />
                
                <div className="text-xs text-gray-500">
                  {revisionSupportedFormatsLabel}
                </div>
              </div>
            </div>
            
            {/* Error Message */}
            {errors.submit && (
              <div className={cn("p-3 rounded-md bg-red-50 border border-red-200", theme.text.primary)}>
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm text-red-700">{errors.submit}</span>
                </div>
              </div>
            )}
            </div>
          </div>
        </ModalContent>
        
        <ModalFooter>
          <div className="flex items-center justify-end gap-3">
            <Button
              variant="outline"
              onClick={handleModalClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="min-w-[100px]"
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Saving...
                </div>
              ) : (
                modalMode === 'create' ? 'Create Revision' : 'Update Revision'
              )}
            </Button>
          </div>
        </ModalFooter>
      </Modal>

      {/* File List Modal */}
      <FileListModal
        isOpen={fileModalOpen}
        onClose={() => {
          setFileModalOpen(false);
          setSelectedRevisionFiles([]);
        }}
        files={selectedRevisionFiles}
        title="Revision File Attachments"
      />

      <Modal
        isOpen={publishDialogOpen}
        onClose={() => {
          if (!isPublishing) closePublishDialog();
        }}
      >
        <ModalHeader>
          <ModalTitle>Publish document</ModalTitle>
          <ModalDescription>
            Confirm publication details. Assigned viewers will see this in My Procedures after
            publish.
          </ModalDescription>
        </ModalHeader>
        <ModalContent>
          <div className="space-y-4 text-sm">
            {publishError && (
              <p className="text-red-600 dark:text-red-400 text-sm">{publishError}</p>
            )}

            <div className={cn('rounded-lg border p-3 grid grid-cols-1 md:grid-cols-2 gap-3', theme.border.light)}>
              <div>
                <div className={cn('text-xs', theme.text.secondary)}>Code</div>
                <div className={theme.text.primary}>{selectedRecord?.code || '—'}</div>
              </div>
              <div>
                <div className={cn('text-xs', theme.text.secondary)}>Title</div>
                <div className={theme.text.primary}>{selectedRecord?.title || '—'}</div>
              </div>
              <div>
                <div className={cn('text-xs', theme.text.secondary)}>Type</div>
                <div className={theme.text.primary}>
                  {selectedRecord?.documentType?.name || selectedRecord?.documentType || '—'}
                </div>
              </div>
              <div>
                <div className={cn('text-xs', theme.text.secondary)}>Department</div>
                <div className={theme.text.primary}>
                  {selectedRecord?.department?.name || selectedRecord?.department || '—'}
                </div>
              </div>
              <div>
                <div className={cn('text-xs', theme.text.secondary)}>Current revision</div>
                <div className={theme.text.primary}>
                  {currentRevisionForPublish?.version ||
                    selectedRecord?.currentRevision?.version ||
                    selectedRecord?.currentRevision ||
                    '—'}
                </div>
              </div>
              <div>
                <div className={cn('text-xs', theme.text.secondary)}>Revision effective</div>
                <div className={theme.text.primary}>
                  {formatDate(currentRevisionForPublish?.dateEffective)}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="publish-date">Published Date *</Label>
                <DateInput
                  id="publish-date"
                  name="publishedAt"
                  value={publishForm.publishedAt}
                  onChange={(e) =>
                    setPublishForm((prev) => ({ ...prev, publishedAt: e.target.value }))
                  }
                  showFormatHint={false}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="publish-status">Published Status *</Label>
                <TextInput
                  id="publish-status"
                  value="Published"
                  readOnly
                  disabled
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="publish-by">Published by</Label>
              <TextInput
                id="publish-by"
                value={
                  getUserFromStorage()?.userDisplayName ||
                  [getUserFromStorage()?.userFirstName, getUserFromStorage()?.userLastName]
                    .filter(Boolean)
                    .join(' ') ||
                  getUserFromStorage()?.userEmail ||
                  '—'
                }
                readOnly
                disabled
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="publish-reason">Published Reason</Label>
              <Textarea
                id="publish-reason"
                rows={3}
                value={publishForm.publishedReason}
                onChange={(e) =>
                  setPublishForm((prev) => ({ ...prev, publishedReason: e.target.value }))
                }
                placeholder="e.g. Approved for distribution to production staff"
              />
            </div>

            <p className="text-amber-700 dark:text-amber-300 text-xs">
              Saves published date, status, and reason on the document. My Procedures uses the
              current revision and published date.
            </p>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button
            type="button"
            variant="outline"
            onClick={closePublishDialog}
            disabled={isPublishing}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handlePublish} disabled={isPublishing}>
            {isPublishing ? 'Publishing…' : 'Publish'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default DocumentRevisionsTab;
